/*
  The comparison cards, and choosing one.

  Shared by the two places an insurer gets picked: the New-policy modal, where the agent is quoting
  live, and the drawer of a policy that was quoted and left — a customer who rang off halfway, or
  one who said they would think about it.

  That second place is why this is its own file. Without it, a policy sitting at "Choosing an
  insurer" had no way to be finished from the dashboard, and the resumability the modal advertises
  ("Close — I'll finish this later") was a promise the rest of the screen could not keep.

  The total is the figure the agent SAYS OUT LOUD, so it is the one thing that should be unmissable.
  The split underneath is for them alone — they need the floor when a customer pushes back — and is
  small and grey so the two can never be confused at a glance.

  No card is marked as recommended. They come in the order the server gave them, which is the same
  order the app draws, and choosing is the customer's.
*/
import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Col, Empty, Modal, Row, Space } from 'antd'
import {
  postRequest,
  insurancePolicyChooseUrl,
  errorNotification,
  successNotification
} from '../../config'

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

export const money = (amount, currency) =>
  amount === null || amount === undefined || amount === ''
    ? '—'
    : `${currency || 'GHS'} ${amount}`

/** Confirm, choose, hand the server's own policy back. Shared so both callers behave identically. */
export const chooseInsurer = (policyId, card, onChosen) => {
  Modal.confirm({
    title: `Go with ${card.insurer}?`,
    content: (
      <span>
        The customer pays <strong>{money(card.total, card.currency)}</strong>. This is the figure
        that gets written onto the policy — changing it afterwards means quoting again.
      </span>
    ),
    okText: `Yes, ${card.insurer}`,
    onOk: async () => {
      const response = await postRequest(
        insurancePolicyChooseUrl(policyId),
        { insurer: card.insurerId },
        'choice',
        () => {}
      )
      if (!response.success) {
        errorNotification(serverSaid(response) || 'That insurer could not be chosen.')
        return
      }
      successNotification(`${card.insurer} it is.`)
      onChosen(response.data.policy)
    }
  })
}

const PolicyPrices = ({ cards, onChoose }) => {
  if (!cards.length) {
    return (
      <Empty
        description={
          <Space direction="vertical">
            <span>No insurer is priced for this cover.</span>
            <span style={{ opacity: 0.65 }}>
              An insurer only appears here when it writes this cover AND has a pricing row for it.
              Setup → Insurers will say which is missing.
            </span>
          </Space>
        }
      />
    )
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Read out the big figure."
        description="It is everything the customer pays. The smaller line is ours and is never itemised to them."
      />
      {cards.map(card => (
        <PriceCard key={card.insurerId} card={card} onChoose={onChoose} />
      ))}
    </Space>
  )
}

PolicyPrices.propTypes = {
  cards: PropTypes.array,
  onChoose: PropTypes.func.isRequired
}

const PriceCard = ({ card, onChoose }) => (
  <Row
    align="middle"
    gutter={12}
    style={{
      border: '1px solid rgba(128,128,128,0.25)',
      borderRadius: 6,
      padding: 12,
      margin: 0
    }}
  >
    <Col flex="auto">
      <Space direction="vertical" size={2}>
        <Space size={8}>
          {card.logoUrl ? (
            <img src={card.logoUrl} alt="" style={{ height: 22, maxWidth: 80 }} />
          ) : null}
          <strong>{card.insurer}</strong>
        </Space>
        <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
          {money(card.total, card.currency)}
        </div>
        <div style={{ opacity: 0.55, fontSize: 12 }}>
          premium {card.premiumAmount} · our fee {card.serviceFeeAmount} · commission{' '}
          {card.commissionAmount}
          {card.commissionKind === 'percent' ? ` (${card.commissionValue}%)` : ''}
        </div>
      </Space>
    </Col>
    <Col>
      <Button type="primary" onClick={() => onChoose(card)}>
        Choose
      </Button>
    </Col>
  </Row>
)

PriceCard.propTypes = {
  card: PropTypes.object.isRequired,
  onChoose: PropTypes.func.isRequired
}

export default PolicyPrices
